import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { ShoppingBag, User, Info, Phone, LogOut, Shield, Search, X, ChevronRight, Sparkles, Truck } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import logo from "../Assets/TantikaLogo.png";
import useAuth from '../Hooks/useAuth';

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAuthenticated } = useAuth();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 24);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Helper function to get correct dashboard path based on user role
  const getDashboardPath = () => {
    // First try to get from AuthContext
    if (user?.role) {
      switch(user.role) {
        case 'artisan':
        case 'pending_artisan':
          return "/artisan/dashboard";
        case 'admin':
        case 'superadmin':
          return "/admin/Addashboard";
        default:
          return "/dashboard";
      }
    }
    
    // If no role in context, check localStorage directly
    try {
      const userStr = localStorage.getItem('tantika_user');
      if (userStr) {
        const storedUser = JSON.parse(userStr);
        
        // Determine role from stored user
        let userRole = storedUser.role;
        
        // If no role, check for artisan indicators
        if (!userRole) {
          const username = storedUser.username || '';
          const email = storedUser.email || '';
          
          if (username.toLowerCase().includes('artisan') || email.toLowerCase().includes('artisan')) {
            userRole = 'artisan';
          } else {
            userRole = 'user';
          }
        }
        
        switch(userRole) {
          case 'artisan':
          case 'pending_artisan':
            return "/artisan/dashboard";
          case 'admin':
          case 'superadmin':
            return "/admin/Addashboard";
          default:
            return "/dashboard";
        }
      }
    } catch (error) {
      console.error('Error determining dashboard path:', error);
    }
    
    // Default fallback
    return "/dashboard";
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Check if user is admin
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  // Active route detection for Shop (rendered as button, not NavLink)
  const isShopActive = location.pathname.startsWith('/shop');

  // Handler for Shop navigation - public, no login required to browse
  const handleShopClick = (e) => {
    e.preventDefault();
    closeMobileMenu();

    // Keep logged-in artisans on their own dashboard, not the customer shop
    if (isAuthenticated && (user?.role === 'artisan' || user?.role === 'pending_artisan')) {
      navigate("/artisan/dashboard");
      return;
    }

    navigate("/shop");
  };

  // Handler for Enter Shop/Go to Shop navigation
  // Authenticated users go to their dashboard, guests go to the public shop
  const handleEnterShopClick = (e) => {
    e.preventDefault();
    closeMobileMenu();

    if (isAuthenticated) {
      navigate(getDashboardPath());
    } else {
      navigate("/shop");
    }
  };

  // Handler for Join Us navigation
  const handleJoinUsClick = (e) => {
    e.preventDefault();
    closeMobileMenu();
    
    if (isAuthenticated) {
      navigate(getDashboardPath());
    } else {
      navigate("/register");
    }
  };

  // Handler for Admin Panel navigation
  const handleAdminPanelClick = (e) => {
    e.preventDefault();
    closeMobileMenu();
    navigate("/admin/Addashboard");
  };

  // Handler for Logout
  const handleLogout = () => {
    logout();
    closeMobileMenu();
    navigate("/");
  };

  // Reusable desktop link classes
  const desktopLinkClass = ({ isActive }) =>
    `group relative py-2 text-sm font-semibold uppercase tracking-wide transition-colors duration-300 ${
      isActive ? "text-clay-600" : "text-cocoa-700 hover:text-clay-600"
    }`;

  const linkUnderline = ({ isActive }) => (
    <span
      className={`absolute left-0 -bottom-0.5 h-0.5 rounded-full bg-gradient-to-r from-saffron-500 to-clay-500 transition-all duration-300 ${
        isActive ? "w-full" : "w-0 group-hover:w-full"
      }`}
    />
  );

  const primaryCtaClass =
    "flex items-center justify-center bg-gradient-to-r from-clay-500 to-saffron-500 text-white rounded-full font-semibold shadow-md hover:shadow-warm-lg hover:-translate-y-0.5 transition-all duration-300";

  const outlineCtaClass =
    "flex items-center justify-center border-2 border-clay-500 text-clay-600 rounded-full font-semibold hover:bg-clay-50 hover:-translate-y-0.5 transition-all duration-300";

  return (
    <>
      {/* Announcement Bar */}
      <div className="bg-gradient-to-r from-clay-600 via-saffron-600 to-clay-600 text-cream-50">
        <div className="container mx-auto px-4 py-2 flex items-center justify-center gap-2 text-xs sm:text-sm font-medium">
          <Truck className="w-4 h-4 shrink-0 hidden sm:block" />
          <span className="truncate">Handcrafted with love in Bengal — every purchase supports local artisans</span>
          <Sparkles className="w-4 h-4 shrink-0 hidden sm:block" />
        </div>
      </div>

      {/* Sticky Header */}
      <nav
        className={`sticky top-0 z-50 border-b transition-all duration-300 ${
          isScrolled
            ? "bg-cream-50/95 backdrop-blur-lg shadow-warm border-cream-200/80"
            : "bg-cream-50/85 backdrop-blur-md border-cream-200/50"
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo with Image */}
            <Link
              to="/"
              className="flex items-center gap-3"
              onClick={closeMobileMenu}
            >
              <img
                src={logo}
                alt="তন্তিকা Logo"
                className="h-12 w-12 md:h-14 md:w-14 object-contain drop-shadow-sm"
              />
              <span className="flex flex-col leading-none">
                <span className="font-bengali text-2xl md:text-3xl font-bold text-gradient">
                  তন্তিকা
                </span>
                <span className="text-[10px] md:text-[11px] uppercase tracking-[0.22em] text-cocoa-500 font-semibold mt-1">
                  Handmade Crafts
                </span>
              </span>
            </Link>

            {/* Desktop Navigation - Center */}
            <div className="hidden md:flex items-center gap-8">
              <NavLink to="/" end className={desktopLinkClass}>
                {linkUnderline}
                Home
              </NavLink>

              {/* Shop Link - Conditionally navigate based on auth */}
              <button
                onClick={handleShopClick}
                className={`group relative py-2 text-sm font-semibold uppercase tracking-wide transition-colors duration-300 bg-transparent border-none cursor-pointer ${
                  isShopActive ? "text-clay-600" : "text-cocoa-700 hover:text-clay-600"
                }`}
              >
                {linkUnderline({ isActive: isShopActive })}
                Shop
              </button>

              <NavLink to="/about" className={desktopLinkClass}>
                {linkUnderline}
                About
              </NavLink>

              <NavLink to="/contact" className={desktopLinkClass}>
                {linkUnderline}
                Contact
              </NavLink>
            </div>

            {/* Right Side - Authentication Buttons */}
            <div className="flex items-center gap-3 md:gap-4">
              {/* Search Icon - Routes to shop */}
              <button
                onClick={handleShopClick}
                aria-label="Search products"
                className="hidden md:inline-flex items-center justify-center w-11 h-11 rounded-full text-cocoa-600 hover:text-clay-600 hover:bg-cream-100 transition-colors"
              >
                <Search className="w-5 h-5" />
              </button>

              {/* Desktop Authentication Buttons - Conditional */}
              <div className="hidden md:flex items-center gap-3">
                {isAuthenticated ? (
                  <>
                    {/* User Profile/Dashboard */}
                    <Link
                      to={getDashboardPath()}
                      className="flex items-center gap-2 text-cocoa-800 hover:text-clay-600 font-semibold px-4 py-2.5 rounded-full border border-cream-300 bg-white/70 hover:border-clay-300 transition-all duration-300"
                      onClick={closeMobileMenu}
                    >
                      <User className="w-4 h-4" />
                      <span className="max-w-[10rem] truncate">{user?.name || "Dashboard"}</span>
                    </Link>

                    {/* ADMIN ONLY: Admin Panel Button */}
                    {isAdmin && (
                      <button
                        onClick={handleAdminPanelClick}
                        className={`${primaryCtaClass} px-5 py-2.5 text-sm`}
                      >
                        <Shield className="w-4 h-4 mr-2" />
                        Admin Panel
                      </button>
                    )}

                    {/* Logout Button */}
                    <button
                      onClick={handleLogout}
                      className="flex items-center border-2 border-red-400 text-red-500 px-5 py-2.5 rounded-full font-semibold hover:bg-red-50 transition-all duration-300"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    {/* Enter Shop Button */}
                    <button
                      onClick={handleEnterShopClick}
                      className={`${primaryCtaClass} px-6 py-2.5 text-sm`}
                    >
                      <User className="w-4 h-4 mr-2" />
                      Enter Shop
                    </button>

                    {/* Join Us Button */}
                    <button
                      onClick={handleJoinUsClick}
                      className={`${outlineCtaClass} px-6 py-2.5 text-sm`}
                    >
                      Join Us
                    </button>
                  </>
                )}
              </div>

              {/* Mobile Menu Button */}
              <button
                className="md:hidden inline-flex items-center justify-center w-11 h-11 rounded-full text-cocoa-700 hover:bg-cream-100 transition-colors"
                onClick={toggleMobileMenu}
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? (
                  <X className="w-7 h-7" />
                ) : (
                  <svg
                    className="w-7 h-7"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25, ease: "easeInOut" }}
              className="md:hidden overflow-hidden border-t border-cream-200 bg-cream-50/95 backdrop-blur-md"
            >
              <div className="container mx-auto px-4 py-4">
                <div className="flex flex-col space-y-1">
                  <MobileLink to="/" icon={<User className="w-5 h-5" />} label="Home" onClick={closeMobileMenu} active={location.pathname === '/'} />

                  {/* Shop in Mobile Menu */}
                  <button
                    onClick={handleShopClick}
                    className={`flex items-center w-full text-left px-4 py-3 rounded-xl font-semibold transition-colors ${
                      isShopActive ? "text-clay-600 bg-clay-50" : "text-cocoa-800 hover:bg-cream-100"
                    }`}
                  >
                    <ShoppingBag className="w-5 h-5 mr-3" />
                    Shop
                    <ChevronRight className="w-4 h-4 ml-auto text-cocoa-400" />
                  </button>

                  <MobileLink to="/about" icon={<Info className="w-5 h-5" />} label="About" onClick={closeMobileMenu} active={location.pathname === '/about'} />
                  <MobileLink to="/contact" icon={<Phone className="w-5 h-5" />} label="Contact" onClick={closeMobileMenu} active={location.pathname === '/contact'} />

                  {/* Mobile Authentication Buttons - Conditional */}
                  <div className="pt-4 mt-2 border-t border-cream-200">
                    <div className="flex flex-col space-y-3">
                      {isAuthenticated ? (
                        <>
                          {/* Dashboard Link */}
                          <Link
                            to={getDashboardPath()}
                            className="flex items-center justify-center bg-clay-50 text-clay-600 px-4 py-3 rounded-xl font-semibold"
                            onClick={closeMobileMenu}
                          >
                            <User className="w-5 h-5 mr-2" />
                            Dashboard
                          </Link>

                          {/* ADMIN ONLY: Admin Panel Button (Mobile) */}
                          {isAdmin && (
                            <button
                              onClick={handleAdminPanelClick}
                              className={`${primaryCtaClass} px-4 py-3`}
                            >
                              <Shield className="w-5 h-5 mr-2" />
                              Admin Panel
                            </button>
                          )}

                          {/* Logout Button */}
                          <button
                            onClick={handleLogout}
                            className="flex items-center justify-center border-2 border-red-400 text-red-500 px-4 py-3 rounded-xl font-semibold hover:bg-red-50"
                          >
                            <LogOut className="w-5 h-5 mr-2" />
                            Logout
                          </button>
                        </>
                      ) : (
                        <>
                          {/* Go to Shop Button */}
                          <button
                            onClick={handleEnterShopClick}
                            className={`${primaryCtaClass} px-4 py-3`}
                          >
                            <User className="w-5 h-5 mr-2" />
                            Go to Shop
                          </button>

                          {/* Join Us Button */}
                          <button
                            onClick={handleJoinUsClick}
                            className={`${outlineCtaClass} px-4 py-3`}
                          >
                            Join Us
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </>
  );
};

// Small helper for mobile menu links
const MobileLink = ({ to, icon, label, onClick, active }) => (
  <Link
    to={to}
    onClick={onClick}
    className={`flex items-center px-4 py-3 rounded-xl font-semibold transition-colors ${
      active ? "text-clay-600 bg-clay-50" : "text-cocoa-800 hover:bg-cream-100"
    }`}
  >
    {icon}
    <span className="ml-3">{label}</span>
    <ChevronRight className="w-4 h-4 ml-auto text-cocoa-400" />
  </Link>
);

export default Navbar;
